/**
 * Campaign Contact Page - Contact form
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Mail, Phone, MapPin, Send, Loader2, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignContact = () => {
  const { slug } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const { data: campaign } = useQuery({
    queryKey: ['campaign', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}`);
      return res.json();
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const res = await fetch(`${API_URL}/api/campaigns/${slug}/contact`, {
        method: 'POST',
        body: submitData
      });

      if (res.ok) {
        toast.success('Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/campaigns/${slug}`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-emerald-600" />
            Contact Us
          </h1>
          {campaign && (
            <p className="text-slate-500 mt-1">{campaign.name}</p>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Send us a Message</CardTitle>
                <CardDescription>
                  Have questions about the project? Want to get involved? We'd love to hear from you.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="How can we help?"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Your message..."
                      rows={5}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign?.contact_email && (
                  <a 
                    href={`mailto:${campaign.contact_email}`}
                    className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{campaign.contact_email}</p>
                    </div>
                  </a>
                )}
                
                {campaign?.contact_phone && (
                  <a 
                    href={`tel:${campaign.contact_phone}`}
                    className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{campaign.contact_phone}</p>
                    </div>
                  </a>
                )}
                
                {campaign?.contact_address && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Address</p>
                      <p className="font-medium">{campaign.contact_address}</p>
                    </div>
                  </div>
                )}

                {campaign?.contact_phone && (
                  <a 
                    href={`https://wa.me/${campaign.contact_phone.replace(/\D/g, '')}?text=Hello, I'm interested in the ${campaign?.name || 'project'}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 mt-4 p-3 bg-green-50 rounded-lg text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    <span className="font-medium">Chat on WhatsApp</span>
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Response Time */}
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="py-4 text-center">
                <p className="text-sm text-emerald-800">
                  We typically respond within <strong>24-48 hours</strong>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignContact;
